// Derives a compliance checklist for an instructor.
// No new tables — reads instructor fields + personnel documents.
import type { InstructorDocument } from "./documents";

export type ChecklistItem = {
  key: string;
  label: string;
  status: "complete" | "missing";
  detail?: string;
};

export function buildComplianceChecklist(input: {
  instructor: any;
  documents: InstructorDocument[];
}): ChecklistItem[] {
  const { instructor, documents } = input;
  const has = (t: string) => documents.some((d) => d.doc_type === t && d.is_current);
  const isVolunteer = instructor.status === "volunteer";

  const items: ChecklistItem[] = [];

  // Background Check — required for BOTH volunteers and paid instructors.
  const bg = !!instructor.background_check_completed || has("background_check");
  items.push({
    key: "background_check",
    label: "Background Check",
    status: bg ? "complete" : "missing",
    detail: instructor.background_check_year
      ? `Year: ${instructor.background_check_year}`
      : undefined,
  });

  // Teaching Agreement — required for everyone; also functions as the
  // governing document for standards of conduct.
  const teachAgree = !!instructor.teaching_agreement_signed;
  items.push({
    key: "teaching_agreement",
    label: "Teaching Agreement",
    status: teachAgree ? "complete" : "missing",
    detail: instructor.teaching_agreement_year ? `Year: ${instructor.teaching_agreement_year}` : undefined,
  });

  // Paid-only compliance items.
  const w9 = isVolunteer || instructor.w9_status === "Received" || instructor.w9_status === "Verified" || instructor.w9_status === "On File";
  items.push({
    key: "w9",
    label: "W-9",
    status: w9 ? "complete" : "missing",
    detail: isVolunteer ? "Volunteer — not required" : instructor.w9_status ?? undefined,
  });

  // Independent Contractor Agreement is no longer tracked — the Teaching
  // Agreement is the governing document. Legacy fields preserved on the
  // instructor record but not surfaced in active compliance.

  // Additional employment documents already required by Silver Sage.
  const cpr = has("cpr_certification");
  items.push({
    key: "cpr",
    label: "CPR Certification",
    status: cpr ? "complete" : "missing",
  });

  return items;
}
